/**
 * Job Status Helpers
 * Centralized utilities for job status handling across the application
 */

// Job Status Constants
export const JOB_STATUSES = {
  ACCEPTING_CANDIDATES: 'accepting_candidates',
  COVERED: 'covered',
  INTERVIEWING: 'interviewing',
  OFFER_OUT: 'offer_out',
  FILLED: 'filled',
  ON_HOLD: 'on_hold',
  CANCELLED: 'cancelled',
  LOST: 'lost',
  DECLINED: 'declined'
};

// Job Status Display Labels
export const JOB_STATUS_LABELS = {
  accepting_candidates: 'Accepting Candidates',
  covered: 'Covered',
  interviewing: 'Interviewing',
  offer_out: 'Offer Out',
  filled: 'Filled',
  on_hold: 'On Hold',
  cancelled: 'Cancelled',
  lost: 'Lost',
  declined: 'Declined'
};

// Get color class for job status badge
export const getJobStatusColor = (status) => {
  const statusLower = status?.toLowerCase().replace(/-/g, '_');
  
  switch (statusLower) {
    case 'accepting_candidates':
      return 'bg-blue-100 text-blue-800';
    case 'covered':
      return 'bg-teal-100 text-teal-800';
    case 'interviewing':
      return 'bg-purple-100 text-purple-800';
    case 'offer_out':
      return 'bg-orange-100 text-orange-800';
    case 'filled':
      return 'bg-green-100 text-green-800';
    case 'on_hold':
      return 'bg-yellow-100 text-yellow-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    case 'lost':
      return 'bg-amber-100 text-amber-800';
    case 'declined':
      return 'bg-gray-100 text-gray-800';
    
    // Legacy statuses (backwards compatibility)
    case 'draft':
      return 'bg-gray-100 text-gray-800';
    case 'open':
      return 'bg-blue-100 text-blue-800'; // Map to Accepting Candidates
    case 'closed':
      return 'bg-green-100 text-green-800'; // Map to Filled
      
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// Get display label for job status
export const getJobStatusLabel = (status) => {
  const statusLower = status?.toLowerCase().replace(/-/g, '_');
  
  // Handle legacy statuses
  const legacyMapping = {
    'draft': 'Draft',
    'open': 'Accepting Candidates',
    'closed': 'Filled'
  };
  
  if (legacyMapping[statusLower]) {
    return legacyMapping[statusLower];
  }
  
  return JOB_STATUS_LABELS[statusLower] || status || 'Unknown';
};

// Check if job is active (accepting submissions)
export const isJobActive = (status) => {
  const statusLower = status?.toLowerCase().replace(/-/g, '_');
  const activeStatuses = ['accepting_candidates', 'covered', 'interviewing', 'offer_out', 'open', 'draft']; // Include legacy statuses
  return activeStatuses.includes(statusLower);
};

// Check if job is closed (no longer active)
export const isJobClosed = (status) => {
  const statusLower = status?.toLowerCase().replace(/-/g, '_');
  const closedStatuses = ['filled', 'cancelled', 'lost', 'declined', 'closed']; // Include legacy status
  return closedStatuses.includes(statusLower);
};

// Get all status options for dropdowns
export const getAllJobStatusOptions = () => {
  return Object.entries(JOB_STATUS_LABELS).map(([value, label]) => ({
    value,
    label
  }));
};
